import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Newspaper, TrendingUp, BookOpen, Bot, FileText, LineChart, ArrowRight, ExternalLink, Sparkles } from "lucide-react";
import { REFERENCE_LIBRARY } from "./referenceLibrary";
import { kipLink } from "../../lib/kipLink";

const critColors = { high: "bg-red-100 text-red-700", medium: "bg-amber-100 text-amber-700", low: "bg-emerald-100 text-emerald-700" };

export default function KipDashboard() {
  const [updates, setUpdates] = useState([]);
  const [news, setNews] = useState([]);
  const [tiles, setTiles] = useState([]);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    api.get("/kip/updates", { params: { limit: 20, criticality: "high" } }).then(({ data }) => setUpdates(data)).catch(() => {});
    api.get("/kip/updates", { params: { limit: 12, tab: "financial_news" } }).then(({ data }) => setNews(data)).catch(() => {});
    api.get("/kip/markets/financial-centre").then(({ data }) => setTiles(data.items || [])).catch(() => {});
    api.get("/kip/markets/companies", { params: { limit: 6 } }).then(({ data }) => setCompanies(data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="kip-dashboard">
      <div>
        <div className="eyebrow">Knowledge Intelligence</div>
        <h1 className="font-sans text-3xl flex items-center gap-2"><Sparkles className="h-6 w-6 text-[#C2A671]"/>KIP Dashboard</h1>
        <p className="text-sm text-slate-600 mt-1">Your daily briefing on regulations, markets and reference material.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { to: "/portal/kip/updates", icon: Newspaper, label: "Updates", count: updates.length, color: "from-rose-500 to-rose-600" },
          { to: "/portal/kip/documents", icon: BookOpen, label: "Repository", count: "All Acts", color: "from-amber-500 to-amber-600" },
          { to: "/portal/kip/markets", icon: TrendingUp, label: "Markets", count: companies.length ? "523+ cos" : "—", color: "from-blue-500 to-blue-600" },
          { to: "/portal/kip/centre", icon: LineChart, label: "Fin Centre", count: tiles.filter(t => t.value).length, color: "from-emerald-500 to-emerald-600" },
          { to: "/portal/kip/assistant", icon: Bot, label: "AI Assistant", count: "Claude 4.5", color: "from-violet-500 to-violet-600" },
          { to: "/portal/kip/bulletin", icon: FileText, label: "Bulletin", count: "Build", color: "from-slate-700 to-slate-800" },
        ].map((q) => (
          <Link key={q.label} to={q.to} className="block group" data-testid={`quick-${q.label.toLowerCase().replace(/[^a-z]/g,'-')}`}>
            <Card className={`p-4 text-white bg-gradient-to-br ${q.color} hover:shadow-lg transition-shadow border-0 h-full`}>
              <q.icon className="h-5 w-5 opacity-80"/>
              <div className="mt-2 text-xs uppercase tracking-wider opacity-90">{q.label}</div>
              <div className="text-2xl font-semibold mt-1">{q.count}</div>
            </Card>
          </Link>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-900">Latest Regulatory Updates</h2>
            <Link to="/portal/kip/updates" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">View all <ArrowRight className="h-3 w-3"/></Link>
          </div>
          <div className="space-y-2">
            {updates.slice(0, 8).map((u) => (
              <div key={u.id} className="flex items-start gap-3 border border-slate-100 rounded p-3" data-testid={`upd-${u.id}`}>
                <Badge className={critColors[u.criticality]}>{u.criticality}</Badge>
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-900">{u.title}</div>
                  <div className="text-xs text-slate-500">{u.source} · {u.issued_on}</div>
                  {u.summary && <p className="text-xs text-slate-600 mt-1 line-clamp-2">{u.summary}</p>}
                  {kipLink(u) && (
                    <a href={kipLink(u).href} target="_blank" rel="noopener noreferrer"
                       className={`text-xs hover:underline inline-flex items-center gap-1 mt-1 ${kipLink(u).dead ? "text-amber-700" : "text-blue-600"}`}
                       data-testid={`upd-src-${u.id}`}>
                      {kipLink(u).label} <ExternalLink className="h-3 w-3"/>
                    </a>
                  )}
                </div>
              </div>
            ))}
            {!updates.length && <div className="text-sm text-slate-400 p-4 text-center">No updates yet — go to Regulatory Updates and click "Seed Samples" or "RBI Feed".</div>}
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-900">Market Snapshot</h2>
            <Link to="/portal/kip/centre" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">View <ArrowRight className="h-3 w-3"/></Link>
          </div>
          <div className="space-y-2">
            {tiles.slice(0, 6).map((t) => (
              <div key={t.label} className="flex items-center justify-between text-sm border-b border-slate-100 py-2">
                <div className="text-slate-700">{t.label}</div>
                <div className="text-right">
                  <div className="font-mono">{t.value != null ? (t.currency === "INR" ? "₹" : "$") + t.value.toLocaleString("en-IN") : "—"}</div>
                  {t.change_pct != null && <div className={`text-xs ${t.change_pct > 0 ? "text-emerald-600" : t.change_pct < 0 ? "text-red-600" : "text-slate-500"}`}>{t.change_pct > 0 ? "+" : ""}{t.change_pct}%</div>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-4" data-testid="financial-news-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2"><Newspaper className="h-5 w-5 text-amber-600"/>Financial News — Business &amp; Markets</h2>
          <Link to="/portal/kip/updates" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1">All news <ArrowRight className="h-3 w-3"/></Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {news.slice(0, 12).map((n) => (
            <a key={n.id} href={kipLink(n)?.href || "#"} target="_blank" rel="noopener noreferrer" title={kipLink(n)?.label}
               className="block p-3 border border-slate-100 rounded hover:border-slate-300 hover:bg-slate-50 transition-colors"
               data-testid={`news-${n.id}`}>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                {n.source} · {n.issued_on}
                {n.link_ok === false ? <span className="text-amber-700 normal-case">· removed by publisher</span> : <ExternalLink className="h-3 w-3"/>}
              </div>
              <div className="text-sm font-medium text-slate-800 mt-1 line-clamp-3">{n.title}</div>
            </a>
          ))}
          {!news.length && (
            <div className="col-span-full text-sm text-slate-400 p-4 text-center border border-dashed border-slate-200 rounded">
              No financial news yet — go to <Link to="/portal/kip/updates" className="text-blue-600 hover:underline">Regulatory Updates</Link> → "Financial News" tab → click "Refresh All Regulators".
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
// Reference Library removed from KIP Dashboard per user request (Phase 16)
// eslint-disable-next-line no-unused-vars
const _REFS_STASH_KEEPS_IMPORT = REFERENCE_LIBRARY;
